import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CanAccess, useDataProvider, useNotify, useTranslate } from "ra-core";
import { Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import type { Project, QuotePlanTemplate } from "../types";
import { PLAN_TEMPLATES_QUERY_KEY, PlanTemplateForm } from "./PlanTemplateForm";

/** "Enganche 20% · Mensualidades 60% · ..." for the non-zero parts of a plan. */
const summarize = (
  template: QuotePlanTemplate,
  translate: (key: string) => string,
) =>
  (
    [
      ["enganche", template.down_payment_pct],
      ["mensualidad", template.monthly_payments_pct],
      ["diferido", template.deferred_payments_pct],
      ["entrega", template.delivery_pct],
      ["credito", template.credit_pct],
    ] as const
  )
    .filter(([, pct]) => pct > 0)
    .map(
      ([concept, pct]) =>
        `${translate(`crm.quoter.concepts.${concept}`)} ${pct}%`,
    )
    .join(" · ");

/**
 * Almikia Epic EQ (TASK-018): "Planes de pago", direccion only. Lists the plan
 * templates per project, with create, edit and archive / reactivate. A
 * template is never deleted; archived ones are hidden unless asked for.
 */
export const PlanTemplateList = () => (
  <CanAccess
    resource="quote_plan_templates"
    action="create"
    accessDenied={<Navigate to="/" replace />}
  >
    <PlanTemplateListContent />
  </CanAccess>
);

PlanTemplateList.path = "/quote_plan_templates";

const PlanTemplateListContent = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider();
  const [showArchived, setShowArchived] = useState(false);
  // undefined = form closed, null = creating, a template = editing it.
  const [formTemplate, setFormTemplate] = useState<
    QuotePlanTemplate | null | undefined
  >(undefined);

  const templates = useQuery({
    queryKey: [PLAN_TEMPLATES_QUERY_KEY, "templates"],
    queryFn: async () =>
      (
        await dataProvider.getList<QuotePlanTemplate>("quote_plan_templates", {
          pagination: { page: 1, perPage: 500 },
          sort: { field: "name", order: "ASC" },
          filter: {},
        })
      ).data,
  });
  const projects = useQuery({
    queryKey: [PLAN_TEMPLATES_QUERY_KEY, "projects"],
    queryFn: async () =>
      (
        await dataProvider.getList<Project>("projects", {
          pagination: { page: 1, perPage: 200 },
          sort: { field: "name", order: "ASC" },
          filter: {},
        })
      ).data,
  });

  /** Archive / reactivate only flips `archived_at`; a template is never deleted. */
  const toggleArchived = async (template: QuotePlanTemplate) => {
    try {
      await dataProvider.update<QuotePlanTemplate>("quote_plan_templates", {
        id: template.id,
        data: {
          archived_at: template.archived_at ? null : new Date().toISOString(),
        },
        previousData: template,
      });
      await queryClient.invalidateQueries({
        queryKey: [PLAN_TEMPLATES_QUERY_KEY],
      });
    } catch {
      notify("crm.quoter.templateForm.save_error", { type: "error" });
    }
  };

  const projectName = (template: QuotePlanTemplate) =>
    (projects.data ?? []).find((project) => project.id === template.project_id)
      ?.name ?? "";
  const visible = (templates.data ?? [])
    .filter((template) => showArchived || !template.archived_at)
    .sort(
      (a, b) =>
        projectName(a).localeCompare(projectName(b), "es") ||
        a.name.localeCompare(b.name, "es"),
    );

  return (
    <div className="mt-2 max-w-3xl mx-auto space-y-4 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <h1 className="text-xl font-semibold">
          {translate("crm.quoter.templates.title")}
        </h1>
        <Button
          type="button"
          className="h-11 sm:h-9"
          onClick={() => setFormTemplate(null)}
        >
          {translate("crm.quoter.templates.new")}
        </Button>
      </div>
      <p className="px-1 text-sm text-muted-foreground">
        {translate("crm.quoter.templates.intro")}
      </p>
      <label className="flex min-h-11 items-center gap-2 px-1 text-sm sm:min-h-0">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
        {translate("crm.quoter.templates.show_archived")}
      </label>

      {templates.error ? (
        <ListErrorState
          message={translate("crm.quoter.templates.error")}
          onRetry={() => templates.refetch()}
        />
      ) : !templates.data ? (
        <ListCardsSkeleton rows={3} />
      ) : visible.length === 0 ? (
        <p className="px-1 text-sm text-muted-foreground">
          {translate("crm.quoter.templates.empty")}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="plan-template-list">
          {visible.map((template) => (
            <li key={template.id}>
              <Card>
                <CardContent className="space-y-2 p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium">{template.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {projectName(template)}
                      {template.archived_at
                        ? ` · ${translate("crm.quoter.templates.archived")}`
                        : ""}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground tabular-nums">
                    {translate(`crm.quoter.planTypes.${template.plan_type}`)}
                    {" · "}
                    {summarize(template, translate)}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-11 sm:h-8"
                      onClick={() => setFormTemplate(template)}
                    >
                      {translate("crm.quoter.templates.edit")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-11 sm:h-8"
                      onClick={() => toggleArchived(template)}
                    >
                      {translate(
                        template.archived_at
                          ? "crm.quoter.templates.reactivate"
                          : "crm.quoter.templates.archive",
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {formTemplate !== undefined ? (
        <PlanTemplateForm
          template={formTemplate}
          projects={projects.data ?? []}
          onClose={() => setFormTemplate(undefined)}
        />
      ) : null}
    </div>
  );
};
